import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteMatchComment, getMatchComments, postMatchComment, toggleMatchReaction } from '../api';
import { useAuth } from '../auth';
import { toast } from '../toast';
import { haptic } from '../haptic';

const REACTIONS = ['🔥', '😂', '😱', '⚽', '😭', '👏'];

export default function CommentsSection({ matchId }: { matchId: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['comments', matchId],
    queryFn: () => getMatchComments(matchId),
    refetchInterval: 30_000,
  });
  const [text, setText] = useState('');

  const postMut = useMutation({
    mutationFn: (body: string) => postMatchComment(matchId, body),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: ['comments', matchId] });
      const prev = qc.getQueryData(['comments', matchId]);
      const tempId = `temp-${Date.now()}`;
      qc.setQueryData(['comments', matchId], (old: any) => old ? ({
        ...old,
        comments: [...(old.comments ?? []), { id: tempId, match_id: matchId, user, body, created_at: new Date().toISOString() }],
      }) : old);
      setText('');
      haptic.tap();
      return { prev };
    },
    onError: (e: Error, _vars, ctx: any) => {
      if (ctx?.prev) qc.setQueryData(['comments', matchId], ctx.prev);
      toast.error(e.message);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['comments', matchId] }),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deleteMatchComment(matchId, id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['comments', matchId] });
      const prev = qc.getQueryData(['comments', matchId]);
      qc.setQueryData(['comments', matchId], (old: any) => old ? ({
        ...old,
        comments: (old.comments ?? []).filter((c: any) => c.id !== id),
      }) : old);
      return { prev };
    },
    onError: (e: Error, _vars, ctx: any) => {
      if (ctx?.prev) qc.setQueryData(['comments', matchId], ctx.prev);
      toast.error(e.message);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['comments', matchId] }),
  });
  const reactMut = useMutation({
    mutationFn: (emoji: string) => toggleMatchReaction(matchId, emoji),
    onMutate: async (emoji) => {
      await qc.cancelQueries({ queryKey: ['comments', matchId] });
      const prev = qc.getQueryData(['comments', matchId]);
      qc.setQueryData(['comments', matchId], (old: any) => {
        if (!old || !user) return old;
        const reactions = [...(old.reactions ?? [])];
        const i = reactions.findIndex((r) => r.emoji === emoji);
        if (i >= 0) {
          const r = { ...reactions[i] };
          if (r.users.includes(user)) {
            r.users = r.users.filter((u: string) => u !== user);
            if (r.users.length === 0) { reactions.splice(i, 1); }
            else reactions[i] = r;
          } else {
            r.users = [...r.users, user];
            reactions[i] = r;
          }
        } else {
          reactions.push({ emoji, users: [user] });
        }
        return { ...old, reactions };
      });
      haptic.select();
      return { prev };
    },
    onError: (e: Error, _vars, ctx: any) => {
      if (ctx?.prev) qc.setQueryData(['comments', matchId], ctx.prev);
      toast.error(e.message);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['comments', matchId] }),
  });

  const comments = data?.comments ?? [];
  const reactions = data?.reactions ?? [];

  return (
    <section className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-xs uppercase tracking-widest text-dim">{'Comments'}</h2>
        <span className="text-[10px] text-dim">{comments.length}</span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {REACTIONS.map((e) => {
          const r = reactions.find((x) => x.emoji === e);
          const mine = !!user && !!r?.users.includes(user);
          const count = r?.users.length ?? 0;
          return (
            <button
              key={e}
              disabled={!user || reactMut.isPending}
              onClick={() => reactMut.mutate(e)}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${mine ? 'bg-pitch-500/15 ring-1 ring-pitch-500/40' : 'bg-ink-800'} disabled:opacity-50`}
            >
              <span>{e}</span>
              {count > 0 && <span className="text-[10px] text-dim">{count}</span>}
            </button>
          );
        })}
      </div>

      <ul className="space-y-1.5 max-h-72 overflow-y-auto" data-allow-select>
        {comments.length === 0 ? (
          <li className="text-[11px] text-dim">No comments yet.</li>
        ) : (
          comments.map((c) => (
            <li key={c.id} className="text-sm flex items-baseline gap-2">
              <span className="text-[10px] uppercase tracking-wider text-dim shrink-0">{c.user}</span>
              <span className="flex-1">{c.body}</span>
              {user === c.user && (
                <button onClick={() => delMut.mutate(c.id)} className="text-[10px] text-dim hover:text-red-400">×</button>
              )}
            </li>
          ))
        )}
      </ul>

      {user ? (
        <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) postMut.mutate(text.trim()); }} className="flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'Add a comment…'}
            maxLength={500}
            className="flex-1 bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk"
            data-allow-select
          />
          <button
            type="submit"
            disabled={!text.trim() || postMut.isPending}
            className="h-9 px-3 rounded-lg bg-pitch-500 text-ink-950 text-xs font-semibold disabled:opacity-50"
          >→</button>
        </form>
      ) : (
        <p className="text-[11px] text-dim">Sign in to comment.</p>
      )}
    </section>
  );
}
